import { useRef, useState, memo, useLayoutEffect, useMemo } from 'react';

import { useRouter } from 'next/navigation';

import { ResourceType, ResourcePermissions } from '@repo/api-client';
import { PermissionsCheck, usePermissionCheck } from '@repo/permissions';
import IconChevronRight from '@repo/ui-icons/IconChevronRight';
import { H6 } from '@repo/ui-kit/components/core/Typography';

import { useUrlWithWorkspacePrefix } from 'global/utils/workspace/useUrlWithWorkspacePrefix';

import { NavigationMenuButton } from '../NavigationMenuButton';
import { useSidebar } from '../SidebarProvider';

import { NavigationMenuSubMenuItem } from './NavigationMenuSubMenuItem';
import {
  ChevronWrapper,
  NavigationMenuListItem,
  NavigationSubMenuList,
  SUBMENU_GAP,
  SUBMENU_TOP_BOTTOM_PADDING,
} from './styles';
import { NavigationMenuItemProps } from './types';
import { isUrlMatch, isActiveOverriden } from './utils';

export const NavigationMenuItem = memo<NavigationMenuItemProps>(
  ({
    label,
    href,
    icon: Icon,
    items,
    activeUrls,
    isCollapseable = false,
    hrefPrefixedByWorkspace,
    requiredPermission,
  }) => {
    const router = useRouter();
    const { hasPermission: hasEditPermissions } = usePermissionCheck(
      ResourcePermissions.DESIGN_EDIT,
      ResourceType.workspace
    );
    const { hasPermission: hasViewPermissions } = usePermissionCheck(
      ResourcePermissions.DESIGN_VIEW,
      ResourceType.workspace
    );
    const availablePermissions: Partial<Record<ResourcePermissions, boolean>> =
      {
        [ResourcePermissions.DESIGN_EDIT]: hasEditPermissions,
        [ResourcePermissions.DESIGN_VIEW]: hasViewPermissions,
      };

    const { optimisticPath, setOptimisticPath } = useSidebar();
    const filteredItems = items?.filter(
      (item) =>
        availablePermissions[
          item.requiredPermission ?? ResourcePermissions.DESIGN_EDIT
        ]
    );

    const hasSubmenu = filteredItems && filteredItems.length > 0;
    const submenuRef = useRef<HTMLUListElement>(null);
    const [submenuHeight, setSubmenuHeight] = useState(0);
    const [canAnimate, setCanAnimate] = useState(false);

    const resolvedHref = useUrlWithWorkspacePrefix({
      href,
      ignorePrefix: !hrefPrefixedByWorkspace,
    });

    // We use the optimistic path to check if menu should be open
    const isMenuActive = useMemo(
      () =>
        Boolean(
          resolvedHref &&
            (isUrlMatch(optimisticPath, resolvedHref) ||
              isActiveOverriden(optimisticPath, activeUrls))
        ),
      [optimisticPath, resolvedHref, activeUrls]
    );

    // This is used to check if the section is active
    const isSectionActive = useMemo(() => {
      return (
        (resolvedHref && isMenuActive) ||
        items?.some(
          (item) =>
            isUrlMatch(optimisticPath, item.href) ||
            isActiveOverriden(optimisticPath, item.activeUrls)
        )
      );
    }, [optimisticPath, resolvedHref, items, isMenuActive]);

    const isSubMenuOpen = Boolean(!isCollapseable || isSectionActive);
    const ariaSubmenuId = `${label.replace(' ', '-').toLowerCase()}-submenu`;

    // Enable animations and measure initial height if needed
    useLayoutEffect(() => {
      if (isSubMenuOpen && submenuRef.current && items) {
        const height =
          submenuRef.current.scrollHeight +
          ((items.length - 1) * SUBMENU_GAP + SUBMENU_TOP_BOTTOM_PADDING * 2);
        setSubmenuHeight(height);
      }
      requestAnimationFrame(() => setCanAnimate(true));
    }, [isSubMenuOpen, items]);

    const handleClick = () => {
      if (!resolvedHref) {
        return;
      }
      // We want to allow menu item click only if resolvedHref for the item exists & either the current menu isn't already selected or a sub-link/breadcrumb is being traversed
      if (!isMenuActive || resolvedHref !== optimisticPath) {
        setOptimisticPath(resolvedHref);
        router.push(resolvedHref);
      }
    };

    return (
      <PermissionsCheck
        requires={requiredPermission ?? ResourcePermissions.DESIGN_EDIT}
        resourceType={ResourceType.workspace}
      >
        <NavigationMenuListItem>
          <NavigationMenuButton
            ariaControls={ariaSubmenuId}
            ariaExpanded={isSubMenuOpen}
            isActive={isMenuActive}
            onClick={handleClick}
          >
            <Icon color="var(--color-text-secondary)" size={16} />
            <H6 as="span" color="secondary">
              {label}
            </H6>
            {hasSubmenu && isCollapseable && (
              <ChevronWrapper
                $isExpanded={isSubMenuOpen}
                className={canAnimate ? 'can-animate' : undefined}
                onClick={handleClick}
              >
                <IconChevronRight
                  color="var(--color-icon-secondary)"
                  size={16}
                />
              </ChevronWrapper>
            )}
          </NavigationMenuButton>

          {hasSubmenu && (
            <NavigationSubMenuList
              ref={submenuRef}
              aria-expanded={isSubMenuOpen}
              className={
                canAnimate && isCollapseable ? 'can-animate' : undefined
              }
              hidden={!isSubMenuOpen}
              id={ariaSubmenuId}
              style={{
                maxHeight:
                  isSubMenuOpen || !isCollapseable ? `${submenuHeight}px` : '0',
              }}
            >
              {filteredItems.map(({ label, href, hrefPrefixedByWorkspace }) => (
                <NavigationMenuSubMenuItem
                  key={label}
                  href={href}
                  hrefPrefixedByWorkspace={hrefPrefixedByWorkspace}
                  isSubMenuOpen={isSubMenuOpen}
                  label={label}
                />
              ))}
            </NavigationSubMenuList>
          )}
        </NavigationMenuListItem>
      </PermissionsCheck>
    );
  }
);

NavigationMenuItem.displayName = 'NavigationMenuItem';
